import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ouropix/ui';
import { Button } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ouropix/ui';
import {
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Eye,
  Filter,
} from 'lucide-react';

export const Route = createFileRoute('/_admin/kyc/review/')({
  component: KycReviewListPage,
});

interface KycSubmissionItem {
  id: string;
  user_id: string;
  doc_type: string;
  status: string;
  user_full_name_masked: string;
  user_cpf_masked: string;
  created_at: string;
  updated_at: string;
  reviewed_at: string;
  reviewed_by_admin_id: string;
}

function mapSubmission(s: any): KycSubmissionItem {
  return {
    id: s.id ?? '',
    user_id: s.userId ?? s.user_id ?? '',
    doc_type: s.docType ?? s.doc_type ?? '',
    status: s.status ?? 'pending',
    user_full_name_masked: s.userFullNameMasked ?? s.user_full_name_masked ?? '--',
    user_cpf_masked: s.userCpfMasked ?? s.user_cpf_masked ?? '--',
    created_at: s.createdAt ?? s.created_at ?? '',
    updated_at: s.updatedAt ?? s.updated_at ?? '',
    reviewed_at: s.reviewedAt ?? s.reviewed_at ?? '',
    reviewed_by_admin_id: s.reviewedByAdminId ?? s.reviewed_by_admin_id ?? '',
  };
}

function formatDate(iso: string): string {
  if (!iso) return '--';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function statusBadge(status: string) {
  switch (status) {
    case 'pending':
      return <Badge variant="outline" className="border-yellow-500 text-yellow-600">Pendente</Badge>;
    case 'approved':
      return <Badge variant="outline" className="border-green-500 text-green-600">Aprovado</Badge>;
    case 'rejected':
      return <Badge variant="outline" className="border-destructive text-destructive">Rejeitado</Badge>;
    case 'additional_info_required':
      return <Badge variant="outline" className="border-orange-500 text-orange-500">Info Adicional</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendente' },
  { value: 'approved', label: 'Aprovado' },
  { value: 'rejected', label: 'Rejeitado' },
  { value: 'additional_info_required', label: 'Info Adicional' },
  { value: 'all', label: 'Todos' },
];

function KycReviewListPage() {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('pending');

  const { data, isLoading } = useQuery<{
    submissions: KycSubmissionItem[];
    total: number;
    total_pages: number;
  }>({
    queryKey: ['admin-kyc-submissions', statusFilter, currentPage],
    queryFn: async () => {
      const params: Record<string, string | number> = {
        page: currentPage,
        limit: PAGE_SIZE,
      };
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const res = await api.get('/admin/kyc/pending', { params });
      const raw = res.data?.submissions ?? res.data?.data ?? [];
      return {
        submissions: (Array.isArray(raw) ? raw : []).map(mapSubmission),
        total: res.data?.total ?? raw.length ?? 0,
        total_pages: res.data?.total_pages ?? Math.ceil((res.data?.total ?? raw.length ?? 0) / PAGE_SIZE),
      };
    },
  });

  const submissions = data?.submissions ?? [];
  const totalPages = data?.total_pages ?? 1;

  const handleStatusChange = (v: string) => {
    setStatusFilter(v);
    setCurrentPage(1);
    queryClient.removeQueries({ queryKey: ['admin-kyc-submissions'] });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-5 w-5 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Revisao KYC</h1>
          <p className="text-muted-foreground text-sm">
            Aprovacao e rejeicao de submissoes de verificacao de identidade
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 rounded-md border bg-card p-4">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Status:</span>
        <Select value={statusFilter} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {data?.total !== undefined && (
          <span className="text-sm text-muted-foreground ml-2">
            {data.total} registro{data.total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuario</TableHead>
              <TableHead className="hidden md:table-cell">CPF</TableHead>
              <TableHead className="hidden sm:table-cell">Documento</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden sm:table-cell">Enviado em</TableHead>
              <TableHead className="hidden lg:table-cell">Revisado em</TableHead>
              <TableHead className="text-right">Acoes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : submissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                  <ShieldCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  Nenhuma submissao encontrada
                </TableCell>
              </TableRow>
            ) : (
              submissions.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>
                    <span className="font-medium">{s.user_full_name_masked}</span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <span className="font-mono text-sm text-muted-foreground">{s.user_cpf_masked}</span>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <Badge variant="secondary" className="text-xs uppercase">{s.doc_type || '--'}</Badge>
                  </TableCell>
                  <TableCell>{statusBadge(s.status)}</TableCell>
                  <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                    {formatDate(s.created_at)}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                    {s.reviewed_at ? formatDate(s.reviewed_at) : '--'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Link to="/kyc/review/$id" params={{ id: s.id }}>
                      <Button variant="ghost" size="sm" title="Ver detalhes">
                        <Eye className="h-3.5 w-3.5 mr-1" />
                        Ver
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Pagina {currentPage} de {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">{currentPage} / {totalPages}</span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
